import React from 'react';
import { Pencil, Target as TargetIcon, Trash2 } from 'lucide-react';

import { ActionMenu } from '../shared/StageKit';
import { METRICS, PERIODS, STATUS, formatValue, paceLine, periodPhrase, targetTitle, unitWord } from './targetMeta';

// AUTO-CRM-32: one target: where it stands, where it should be by now, and
// what each remaining day needs.
const MILESTONES = [25, 50, 75];

export function TargetBar({ target, size = 'md', showExpected = true }) {
  const progress = target.progress || {};
  const goal = target.target_value || 1;
  const percent = Math.min(100, (progress.actual / goal) * 100);
  const expected = showExpected && !['done', 'missed'].includes(progress.status) && progress.expected_by_now > 0 ? Math.min(100, (progress.expected_by_now / goal) * 100) : null;
  const tone = STATUS[progress.status]?.tone || 'ok';
  return (
    <div className={`crm-tbar is-${tone} is-${size}`} role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(percent)} aria-label={`${targetTitle(target)}: ${Math.round((progress.actual / goal) * 100)}% of the target`}>
      <span className="crm-tbar-fill" style={{ width: `${percent}%` }} />
      {MILESTONES.map((mark) => <i key={mark} className={`crm-tbar-tick${percent >= mark ? ' is-passed' : ''}`} style={{ left: `${mark}%` }} aria-hidden="true" />)}
      {expected !== null && <b className="crm-tbar-mark" style={{ left: `${expected}%` }} title={`Should be at ${formatValue(target, progress.expected_by_now)} by now`} aria-hidden="true" />}
    </div>
  );
}

function Sparkline({ series = [] }) {
  const max = Math.max(1, ...series.map((point) => point.value));
  if (series.length < 2) return <span className="crm-spark is-empty" aria-hidden="true" />;
  return (
    <span className="crm-spark" aria-hidden="true">
      {series.slice(-14).map((point, index, list) => <i key={point.date} className={index === list.length - 1 ? 'is-today' : ''} style={{ height: `${Math.max(point.value ? 12 : 4, (point.value / max) * 100)}%` }} title={`${point.date}: ${point.value}`} />)}
    </span>
  );
}

export function nextMilestone(target) {
  const percent = ((target.progress?.actual || 0) / (target.target_value || 1)) * 100;
  const next = [...MILESTONES, 100].find((mark) => percent < mark);
  const value = (target.target_value * next) / 100;
  return next ? { mark: next, value: target.metric === 'revenue' ? value : Math.ceil(value) } : null;
}

export default function TargetCard({ target, onEdit, onDelete, canManage = false, showOwner = true, finished = false }) {
  const progress = target.progress || {};
  const status = STATUS[progress.status] || STATUS.on_track;
  const Icon = METRICS[target.metric]?.icon || TargetIcon;
  const period = PERIODS[target.period] || PERIODS.monthly;
  const next = nextMilestone(target);
  const owner = target.user ? target.user.name : 'Company';
  return (
    <li className={`crm-target is-${status.tone}`}>
      <header className="crm-target-head">
        <span className="crm-target-icon" aria-hidden="true"><Icon size={17} /></span>
        <div className="crm-target-id">
          <h3 title={targetTitle(target)}>{targetTitle(target)}</h3>
          <span>{period.label}{showOwner ? ` · ${owner}` : ''}{target.pipeline && target.metric !== 'stage_reached' ? ` · ${target.pipeline.name}` : ''}</span>
        </div>
        <span className={`crm-target-status is-${status.tone}`}>{status.label}</span>
        {canManage && <ActionMenu label={`Actions for ${targetTitle(target)}`} items={[
          { label: 'Edit target', icon: Pencil, onClick: () => onEdit?.(target) },
          'separator',
          { label: 'Remove target', icon: Trash2, danger: true, onClick: () => onDelete?.(target) },
        ]} />}
      </header>
      <div className="crm-target-numbers">
        <b>{formatValue(target, progress.actual)}</b>
        <span>of {formatValue(target, target.target_value)} {unitWord(target, target.target_value)} {periodPhrase(target.period, finished)}</span>
        <em>{Math.round(progress.percent || 0)}%</em>
      </div>
      <TargetBar target={target} showExpected={!finished} />
      <div className="crm-target-marks">
        {progress.status !== 'done' && progress.expected_by_now > 0 && !finished
          ? <span><i className="crm-target-mark-key" aria-hidden="true" />By now: {formatValue(target, target.metric === 'revenue' ? progress.expected_by_now : Math.round(progress.expected_by_now))}</span>
          : <span>{progress.status === 'done' ? 'Every milestone reached' : finished ? 'Period ended' : 'Just started'}</span>}
        {next && <span>Next milestone {next.mark}% · {formatValue(target, next.value)}</span>}
      </div>
      <p className="crm-target-pace">{paceLine(target, finished)}</p>
      <footer className="crm-target-foot">
        <Sparkline series={progress.series} />
        <span>{target.period === 'daily' ? 'Last 7 days' : finished ? `That ${period.noun}` : `So far ${period.this}`} · last {period.noun}: <b>{formatValue(target, progress.previous_actual)}</b></span>
      </footer>
    </li>
  );
}
